import React, { useState } from 'react';
import { CallInsightsAnalysis, CallInsightsSection, ManagerReview } from '../adherence/types';
import ObjFaqAdherenceSection from '../adherence/ObjFaqAdherenceSection';
import LandmineAdherenceSection from '../adherence/LandmineAdherenceSection';
import CompetitorAdherenceSection from '../adherence/CompetitorAdherenceSection';
import ManagerReviewPanel from '../adherence/ManagerReviewPanel';

interface KnowledgeBaseSectionProps {
  sessionId: string;
  callInsights: CallInsightsAnalysis;
  isAdmin: boolean;
  onReviewSaved: () => void;
  onNavigateToTimestamp?: (ts: string) => void;
  /** When provided, this sub-accordion will be auto-expanded on mount. */
  initialExpandedAccordion?: string;
}

interface AccordionConfig {
  id: CallInsightsSection;
  title: string;
  itemCount: number;
  overallSummary: string;
}

const KnowledgeBaseSection: React.FC<KnowledgeBaseSectionProps> = ({
  sessionId,
  callInsights,
  isAdmin,
  onReviewSaved,
  onNavigateToTimestamp,
  initialExpandedAccordion,
}) => {
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(
    initialExpandedAccordion ?? null
  );

  const toggleAccordion = (id: string) => {
    setExpandedAccordion((prev) => (prev === id ? null : id));
  };

  const accordions: AccordionConfig[] = [
    {
      id: 'objection_faq',
      title: 'Objections / FAQs',
      itemCount: callInsights?.objection_faq?.items?.length ?? 0,
      overallSummary: callInsights?.objection_faq?.overall_summary ?? '',
    },
    {
      id: 'landmine',
      title: 'Landmines',
      itemCount: callInsights?.landmine?.items?.length ?? 0,
      overallSummary: callInsights?.landmine?.overall_summary ?? '',
    },
    {
      id: 'competitor',
      title: 'Competitors',
      itemCount: callInsights?.competitor?.items?.length ?? 0,
      overallSummary: callInsights?.competitor?.overall_summary ?? '',
    },
  ];

  const renderAccordionBody = (id: CallInsightsSection) => {
    switch (id) {
      case 'objection_faq':
        return (
          <ObjFaqAdherenceSection
            items={callInsights?.objection_faq?.items ?? []}
            overall_summary={callInsights?.objection_faq?.overall_summary ?? ''}
            sessionId={sessionId}
            managerReview={callInsights?.manager_review}
            isAdmin={isAdmin}
            onReviewSaved={onReviewSaved}
            onNavigateToTimestamp={onNavigateToTimestamp}
          />
        );
      case 'landmine':
        return (
          <LandmineAdherenceSection
            items={callInsights?.landmine?.items ?? []}
            overall_summary={callInsights?.landmine?.overall_summary ?? ''}
            sessionId={sessionId}
            managerReview={callInsights?.manager_review}
            isAdmin={isAdmin}
            onReviewSaved={onReviewSaved}
            onNavigateToTimestamp={onNavigateToTimestamp}
          />
        );
      case 'competitor':
        return (
          <CompetitorAdherenceSection
            items={callInsights?.competitor?.items ?? []}
            overall_summary={callInsights?.competitor?.overall_summary ?? ''}
            sessionId={sessionId}
            managerReview={callInsights?.manager_review}
            isAdmin={isAdmin}
            onReviewSaved={onReviewSaved}
            onNavigateToTimestamp={onNavigateToTimestamp}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {accordions.map((accordion) => {
        const isExpanded = expandedAccordion === accordion.id;

        return (
          <div key={accordion.id} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Accordion header */}
            <button
              onClick={() => toggleAccordion(accordion.id)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              type="button"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-gray-800">{accordion.title}</span>
                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                  {accordion.itemCount}
                </span>
              </div>
              <div className="flex items-center gap-3">
                {accordion.overallSummary && !isExpanded && (
                  <span className="text-xs text-gray-500 max-w-sm truncate hidden md:inline">
                    {accordion.overallSummary}
                  </span>
                )}
                <span
                  className="text-gray-400 text-xs transition-transform duration-150"
                  style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', display: 'inline-block' }}
                >
                  &#9660;
                </span>
              </div>
            </button>

            {/* Accordion body */}
            {isExpanded && (
              <div className="p-4 border-t border-gray-200">
                {renderAccordionBody(accordion.id)}

                {/* Per-accordion manager notes */}
                <ManagerReviewPanel
                  sessionId={sessionId}
                  currentSection={accordion.id}
                  managerReview={callInsights?.manager_review}
                  isAdmin={isAdmin}
                  onReviewSaved={onReviewSaved}
                />
              </div>
            )}
          </div>
        );
      })}

    </div>
  );
};

export default KnowledgeBaseSection;
